import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../engine/api/context';
import { useAction, useCreate, useMeta, useUpdate } from '../../engine/api/hooks';
import { useApiQuery } from '../../engine/api/hooks';
import { ApiError } from '../../engine/api/client';
import { fieldOf } from '../../engine/meta/types';
import { Button, IconButton } from '../../engine/design/primitives/Button';
import { Input, Select, Switch } from '../../engine/design/primitives/Input';
import { DatePicker } from '../../engine/design/primitives/DatePicker';
import { Combobox } from '../../engine/design/primitives/Combobox';
import { Badge, Skeleton, type Tone } from '../../engine/design/primitives/Misc';
import { useToast } from '../../engine/design/primitives/Toast';
import { RelationPicker } from '../../engine/fields/RelationPicker';
import { DurationEditor } from '../../engine/fields/registry';
import { NotesPanel } from '../../engine/views/NotesPanel';
import { useInspector } from '../../engine/views/Inspector';
import { formatDate, formatMinutes } from '../../lib/format';
import { DEP_LABEL, STATUS_TONE, day, durationField, relField, taskTimeKey, todayIso, useProjectDetail, type DepType, type TaskRow, type TimeEntryRow } from './data';

// The task inspector (right-hand dock): every field editable in place, dependencies with a
// picker, subtasks, the time log with a "Log time" duration editor, the linked ticket, and
// comments through the platform NotesPanel. Reads the shared project detail query, so it
// stays live while the views behind it change.

export function TaskInspector({ taskId, projectId }: { taskId: string; projectId: string }) {
  const detail = useProjectDetail(projectId);
  const meta = useMeta('project-tasks').data;
  const task = detail.data?.tasks.find((t) => t.id === taskId);
  if (!detail.data || !meta) return <Skeleton lines={8} />;
  if (!task) return <p className="hf-muted">This task is no longer part of the project.</p>;
  return <Inspector task={task} projectId={projectId} refresh={detail.refresh} tasks={detail.data.tasks} phases={detail.data.phases} deps={detail.data.dependencies} statusField={fieldOf(meta, 'status')} priorityField={fieldOf(meta, 'priority')} assigneeField={fieldOf(meta, 'assignee_tech_id')} ticketField={fieldOf(meta, 'ticket_id')} />;
}

const technicianField = relField('technician_id', 'Technician', 'technicians');
const minutesField = durationField('minutes', 'Duration');
const estimateField = durationField('estimate_minutes', 'Estimate');

function Inspector({ task, projectId, refresh, tasks, phases, deps, statusField, priorityField, assigneeField, ticketField }: {
  task: TaskRow;
  projectId: string;
  refresh: () => void;
  tasks: TaskRow[];
  phases: { id: string; name: string }[];
  deps: { id: string; predecessor_id: string; successor_id: string; type: DepType; lag_days: number }[];
  statusField?: ReturnType<typeof fieldOf>;
  priorityField?: ReturnType<typeof fieldOf>;
  assigneeField?: ReturnType<typeof fieldOf>;
  ticketField?: ReturnType<typeof fieldOf>;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const inspector = useInspector();
  const update = useUpdate<TaskRow>('project-tasks', { toast: { success: 'Saved' }, onSuccess: refresh });
  const complete = useAction('project-tasks', 'complete', { toast: { success: `${task.ref} completed` }, onSuccess: refresh });
  const addDep = useAction('project-tasks', 'dependencies', { toast: { success: 'Dependency added' }, onSuccess: refresh });
  const createSub = useCreate('project-tasks', { toast: { success: 'Subtask added' }, onSuccess: refresh });
  const timeQ = useApiQuery<{ data: TimeEntryRow[]; actual_minutes: number }>(taskTimeKey(task.id), `/v1/project-tasks/${task.id}/time`);
  const logTime = useAction('project-tasks', 'time', {
    toast: { success: 'Time logged' },
    invalidate: ['time-entries', 'timesheets'],
    onSuccess: () => {
      refresh();
      void qc.invalidateQueries({ queryKey: taskTimeKey(task.id) });
      setMinutes(null);
      setNote('');
    },
  });

  const [title, setTitle] = useState(task.title);
  const [depPred, setDepPred] = useState<string | null>(null);
  const [depType, setDepType] = useState<DepType>('fs');
  const [subTitle, setSubTitle] = useState('');
  const [tech, setTech] = useState<string | null>(task.assignee_tech_id);
  const [minutes, setMinutes] = useState<number | null>(null);
  const [on, setOn] = useState(todayIso());
  const [note, setNote] = useState('');
  const [billable, setBillable] = useState(task.is_billable);

  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const predecessors = deps.filter((d) => d.successor_id === task.id);
  const successors = deps.filter((d) => d.predecessor_id === task.id);
  const children = tasks.filter((t) => t.parent_task_id === task.id);
  const candidates = tasks.filter((t) => t.id !== task.id && !predecessors.some((d) => d.predecessor_id === t.id)).map((t) => ({ value: t.id, label: `${t.ref} ${t.title}`, sub: t.status.replace(/_/g, ' ') }));
  const patch = (body: Partial<TaskRow>) => update.mutate({ id: task.id, body, previous: Object.fromEntries(Object.keys(body).map((k) => [k, task[k as keyof TaskRow]])) });
  const open = (t: TaskRow) => inspector.show({ key: `task:${t.id}`, title: t.ref, subtitle: 'Task', content: <TaskInspector taskId={t.id} projectId={projectId} /> });
  const removeDep = async (id: string) => {
    try {
      await api.resource('project-tasks').action(`dependencies/${id}`, { id: task.id, method: 'DELETE' });
      toast({ title: 'Dependency removed' });
      refresh();
    } catch (e) {
      toast({ kind: 'error', title: 'Could not remove dependency', detail: e instanceof ApiError ? e.detail : String(e) });
    }
  };
  const tone = (s: string) => (statusField?.enum?.find((o) => o.value === s)?.tone as Tone | undefined) ?? STATUS_TONE[s as keyof typeof STATUS_TONE];

  return (
    <div className="hf-prj-inspector">
      <div className="hf-stack">
        <Input className="title-input" aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => title.trim() && title !== task.title && patch({ title: title.trim() })} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
        <div className="hf-row" style={{ flexWrap: 'wrap' }}>
          <Badge tone={tone(task.status)}>{statusField?.enum?.find((o) => o.value === task.status)?.label ?? task.status}</Badge>
          <Badge tone="muted" dot={false}>{task.kind}</Badge>
          <span className="hf-muted hf-mono">{task.ref}</span>
          {task.status !== 'done' && task.status !== 'cancelled' && (
            <Button size="sm" variant="primary" icon="check" busy={complete.isPending} onClick={() => complete.mutate({ id: task.id })} style={{ marginLeft: 'auto' }}>
              Complete
            </Button>
          )}
        </div>
      </div>

      <dl className="hf-kv">
        <div>
          <dt>Status</dt>
          <dd>
            <Select size="sm" aria-label="Status" value={task.status} onChange={(e) => patch({ status: e.target.value as TaskRow['status'] })} options={(statusField?.enum ?? []).map((o) => ({ value: o.value, label: o.label }))} />
          </dd>
        </div>
        <div>
          <dt>Priority</dt>
          <dd>
            <Select size="sm" aria-label="Priority" value={task.priority} onChange={(e) => patch({ priority: e.target.value })} options={(priorityField?.enum ?? []).map((o) => ({ value: o.value, label: o.label }))} />
          </dd>
        </div>
        <div>
          <dt>Phase</dt>
          <dd>
            <Select size="sm" aria-label="Phase" value={task.phase_id ?? ''} placeholder="No phase" onChange={(e) => patch({ phase_id: e.target.value || null })} options={phases.map((p) => ({ value: p.id, label: p.name }))} />
          </dd>
        </div>
        {assigneeField && (
          <div>
            <dt>Assignee</dt>
            <dd>
              <RelationPicker field={assigneeField} value={task.assignee_tech_id} size="sm" aria-label="Assignee" onChange={(v) => patch({ assignee_tech_id: v })} />
            </dd>
          </div>
        )}
        <div>
          <dt>Start</dt>
          <dd>
            <DatePicker size="sm" aria-label="Start date" value={day(task.start_date)} onChange={(iso) => patch({ start_date: iso })} />
          </dd>
        </div>
        <div>
          <dt>Due</dt>
          <dd>
            <DatePicker size="sm" aria-label="Due date" value={day(task.due_date)} onChange={(iso) => patch({ due_date: iso })} />
          </dd>
        </div>
        <div>
          <dt>Estimate</dt>
          <dd>
            <DurationEditor field={estimateField} value={task.estimate_minutes} size="sm" aria-label="Estimate" onChange={(v) => v != null && v !== task.estimate_minutes && patch({ estimate_minutes: Number(v) })} />
          </dd>
        </div>
        <div>
          <dt>Logged</dt>
          <dd>
            <span className="hf-num">{formatMinutes(task.actual_minutes)}</span>
            {task.estimate_minutes > 0 && <span className="hf-muted"> of {formatMinutes(task.estimate_minutes)}</span>}
          </dd>
        </div>
        {ticketField && (
          <div>
            <dt>Ticket</dt>
            <dd>
              <RelationPicker field={ticketField} value={task.ticket_id} size="sm" aria-label="Linked ticket" onChange={(v) => patch({ ticket_id: v })} />
            </dd>
          </div>
        )}
        <div>
          <dt>Billable</dt>
          <dd>
            <Switch checked={task.is_billable} onChange={(e) => patch({ is_billable: e.target.checked })} label={task.is_billable ? 'Billable' : 'Non-billable'} />
          </dd>
        </div>
      </dl>

      <section>
        <div className="hf-caps">Dependencies</div>
        <ul className="hf-prj-list" aria-label="Predecessors">
          {predecessors.length === 0 && successors.length === 0 && <li className="hf-muted">None</li>}
          {predecessors.map((d) => {
            const p = byId.get(d.predecessor_id);
            return (
              <li key={d.id}>
                <span className="hf-muted">after</span>
                <button type="button" className="lbl grow" onClick={() => p && open(p)}>
                  {p ? `${p.ref} ${p.title}` : d.predecessor_id}
                </button>
                <Badge tone="muted" dot={false}>{DEP_LABEL[d.type]}{d.lag_days ? ` +${d.lag_days}d` : ''}</Badge>
                <IconButton icon="x" label="Remove dependency" size="sm" onClick={() => void removeDep(d.id)} />
              </li>
            );
          })}
          {successors.map((d) => {
            const s = byId.get(d.successor_id);
            return (
              <li key={d.id}>
                <span className="hf-muted">before</span>
                <button type="button" className="lbl grow" onClick={() => s && open(s)}>
                  {s ? `${s.ref} ${s.title}` : d.successor_id}
                </button>
                <Badge tone="muted" dot={false}>{DEP_LABEL[d.type]}</Badge>
              </li>
            );
          })}
        </ul>
        <div className="hf-row" style={{ marginTop: 'var(--s-2)' }}>
          <div className="hf-grow">
            <Combobox<string> value={depPred} onChange={(v) => setDepPred(v)} options={candidates} placeholder="Depends on…" size="sm" aria-label="Predecessor task" />
          </div>
          <Select size="sm" aria-label="Dependency type" value={depType} onChange={(e) => setDepType(e.target.value as DepType)} options={(Object.keys(DEP_LABEL) as DepType[]).map((k) => ({ value: k, label: k.toUpperCase() }))} />
          <Button size="sm" icon="git-branch" disabled={!depPred} busy={addDep.isPending} onClick={() => depPred && addDep.mutate({ id: task.id, body: { predecessor_id: depPred, type: depType } }, { onSuccess: () => setDepPred(null) })}>
            Add
          </Button>
        </div>
      </section>

      <section>
        <div className="hf-caps">Subtasks</div>
        <ul className="hf-prj-list" aria-label="Subtasks">
          {children.length === 0 && <li className="hf-muted">None</li>}
          {children.map((c) => (
            <li key={c.id}>
              <Badge tone={tone(c.status)} dot={false}>{c.status.replace(/_/g, ' ')}</Badge>
              <button type="button" className="lbl grow" onClick={() => open(c)}>
                {c.ref} {c.title}
              </button>
              <span className="hf-num hf-muted">{formatMinutes(c.actual_minutes)}</span>
            </li>
          ))}
        </ul>
        <form
          className="hf-row"
          style={{ marginTop: 'var(--s-2)' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!subTitle.trim()) return;
            createSub.mutate({ project_id: projectId, parent_task_id: task.id, phase_id: task.phase_id, title: subTitle.trim() }, { onSuccess: () => setSubTitle('') });
          }}
        >
          <Input size="sm" className="hf-grow" aria-label="New subtask" placeholder="Add a subtask…" value={subTitle} onChange={(e) => setSubTitle(e.target.value)} />
          <Button size="sm" type="submit" icon="plus" disabled={!subTitle.trim()} busy={createSub.isPending}>
            Add
          </Button>
        </form>
      </section>

      <section>
        <div className="hf-caps">Time</div>
        <ul className="hf-prj-list" aria-label="Time log">
          {(timeQ.data?.data ?? []).length === 0 && <li className="hf-muted">No time logged yet</li>}
          {(timeQ.data?.data ?? []).map((e) => (
            <li key={e.id}>
              <span className="hf-num">{formatDate(e.occurred_on)}</span>
              <span className="grow">
                {e.technician_name ?? '—'}
                {e.note ? <span className="hf-muted"> · {e.note}</span> : null}
              </span>
              {!e.billable && <Badge tone="muted" dot={false}>non-billable</Badge>}
              <span className="hf-num">{formatMinutes(e.minutes)}</span>
            </li>
          ))}
        </ul>
        <form
          className="hf-stack"
          style={{ marginTop: 'var(--s-3)' }}
          aria-label="Log time"
          onSubmit={(e) => {
            e.preventDefault();
            if (!tech || !minutes) return;
            logTime.mutate({ id: task.id, body: { technician_id: tech, minutes, occurred_on: on, note: note || undefined, billable } });
          }}
        >
          <div className="hf-row">
            <div className="hf-grow">
              <RelationPicker field={technicianField} value={tech} size="sm" aria-label="Technician" onChange={(v) => setTech(v)} />
            </div>
            <div style={{ width: 130 }}>
              <DurationEditor field={minutesField} value={minutes} size="sm" aria-label="Duration" onChange={(v) => setMinutes(v == null ? null : Number(v))} />
            </div>
            <DatePicker size="sm" aria-label="Work date" value={on} onChange={(iso) => setOn(iso ?? todayIso())} />
          </div>
          <div className="hf-row">
            <Input size="sm" className="hf-grow" aria-label="Note" placeholder="What was done" value={note} onChange={(e) => setNote(e.target.value)} />
            <Switch checked={billable} onChange={(e) => setBillable(e.target.checked)} label="Billable" />
            <Button size="sm" type="submit" variant="primary" icon="timer" disabled={!tech || !minutes} busy={logTime.isPending}>
              Log time
            </Button>
          </div>
        </form>
      </section>

      <section>
        <div className="hf-caps">Comments</div>
        <NotesPanel entityType="project_task" entityId={task.id} />
      </section>
    </div>
  );
}
